import AdImageHandlerInstance from '@shared/utils/adImageHandler';
import AdUtils from '@shared/utils/ads';
import Channel from './channel';
import ChannelUtils from '../utils/channels';
import Content from '../types/enums/content';
import EpgEvent from './epgEvent';
import FastEvent from './fastEvent';
import ImageUtils from '../utils/image';
import MediaProduction from './mediaProduction';
import { ISliderData } from '../types/interfaces/slider';
import {
  IEPGEventRequest,
  IFastEventRequest,
  IMediaProductionEventRequest,
} from '../types/interfaces/event';

/**
 * Represents a promo item in the application.
 * Handles different types of content including channels, events, media productions, ads, and more.
 * Manages image URLs, impressions tracking, and ad image fetching.
 */
class Slider {
  addon?: string;
  channel?: Channel;
  content?: FastEvent | EpgEvent | MediaProduction;
  corner?: string;
  eventId?: string;
  id: string;
  link?: string;
  linkImageTV?: string;
  plan?: string;
  type: string | Content;
  img: string;
  extendedImg?: string;
  impressions: {
    bannerFocus: string[];
    promoFocus: string[];
    promoSelection: string[];
  };
  ogImg: string;
  sliderImg: string;

  /**
   * Creates a new Slider instance.
   *
   * @param {ISliderData} sliderData - The promo data object containing all slider properties
   * @param {string} [sliderData.id] - Unique identifier for the slider
   * @param {string} [sliderData.eventId] - Event identifier
   * @param {string} [sliderData.channelName] - Name of the associated channel
   * @param {string} [sliderData.image] - Image URL for the slider
   * @param {string} [sliderData.imageTV] - TV-specific image URL
   * @param {string} [sliderData.link] - Link URL (used for ads)
   * @param {string} [sliderData.linkImageTV] - TV-specific link image URL
   * @param {string} [sliderData.type] - Type of content (app, corner, channel, addon, etc.)
   * @param {string} [sliderData.corner] - Corner identifier
   * @param {IEPGEventRequest | IFastEventRequest | IMediaProductionEventRequest} [sliderData.relatedMedia] - Related media content
   * @param {string} [sliderData.addon] - Addon identifier
   * @param {string} [sliderData.plan] - Plan identifier
   * @param {number} index - Index of the slider in the list (used as fallback ID)
   */
  constructor(
    {
      id,
      eventId,
      channelName,
      image,
      imageTV,
      link,
      linkImageTV,
      type,
      corner,
      relatedMedia,
      addon,
      plan,
    }: ISliderData,
    index: number
  ) {
    this.addon = addon;
    this.corner = corner;
    this.eventId = eventId;
    this.id = id || eventId || channelName || `${index}`;
    this.plan = plan;
    this.type =
      type === 'app'
        ? Content.APP
        : type === 'corner'
        ? Content.CORNER
        : type === 'channel'
        ? Content.CHANNEL
        : type === 'addon'
        ? Content.ADDON
        : type;

    this.img = ImageUtils.getImage(image, 'slider');
    this.ogImg = ImageUtils.getImage(image);
    this.sliderImg = ImageUtils.getImage(imageTV || image);

    this.impressions = {
      bannerFocus: [],
      promoFocus: [],
      promoSelection: [],
    };

    if (channelName) {
      this.channel = ChannelUtils.findChannelByName(channelName);
    }

    if (link) {
      this.link = AdUtils.parseMacros(link);
      if (type === 'video_ads') {
        this.link = AdUtils.parsePrerolls(this.link);
      }
    }

    if (linkImageTV) {
      this.linkImageTV = AdUtils.parseMacros(linkImageTV);
    }

    switch (relatedMedia?.kind) {
      case Content.EPG_EVENT:
        this.content = new EpgEvent(relatedMedia as IEPGEventRequest);
        break;
      case Content.FAST_EVENT:
        this.content = new FastEvent(relatedMedia as IFastEventRequest);
        break;
      case Content.MEDIA_PRODUCTION:
        this.content = new MediaProduction(
          relatedMedia as IMediaProductionEventRequest
        );
        break;
    }
  }

  /**
   * Fetches and updates the main slider image with an ad image.
   * Updates the slider's image URL and impression trackers based on the ad response.
   * Uses the link property to fetch the ad image.
   */
  imageAds = async () => {
    const { url, selectionTrackers, impressionTrackers } =
      await AdUtils.fetchAdImage(this.link, {
        clicks: this.impressions.promoSelection,
        impressions: this.impressions.promoFocus,
      });
    if (url) {
      this.img = url;
    }
    if (selectionTrackers) {
      this.impressions.promoSelection = selectionTrackers;
    }
    if (impressionTrackers) {
      this.impressions.promoFocus = impressionTrackers;
    }
  };

  /**
   * Fetches and updates the extended/TV slider (Big one that appears when menu is open) image with an ad image.
   * Updates the slider's TV image URL and banner focus impression trackers.
   * Uses the linkImageTV property to fetch the ad image.
   */
  extendedImageAds = async () => {
    const { url, impressionTrackers } = await AdUtils.fetchAdImage(
      this.linkImageTV,
      {
        clicks: [],
        impressions: this.impressions.bannerFocus,
      }
    );
    if (url) {
      this.sliderImg = url;
    }
    if (impressionTrackers) {
      this.impressions.bannerFocus = impressionTrackers;
    }
  };

  /**
   * Checks if the slider's content represents a live event
   * Compares the current timestamp with the event's start date.
   *
   * @returns {boolean} True if the event is currently playing on the channel
   */
  isCurrentEvent() {
    const currentDate = Date.now();
    if (
      this.content?.date &&
      currentDate > this.content?.date?.start &&
      currentDate < this.content?.date?.start
    ) {
      return true;
    }
    return false;
  }
}

export default Slider;
